import { useEffect, useRef } from 'react';
import { extendSingleLatLngBounds } from 'app/components/VisGLMap/VisGLMap.helpers';

interface MapPin {
  type: 'pin';
  center: google.maps.LatLngLiteral;
}

interface MapCircle {
  type: 'circle';
  center: google.maps.LatLngLiteral;
  radius: number;
}

export type MapElement = MapPin | MapCircle;

export const useMapInitialZoom = ({
  map,
  padding = 0,
  mapElements = [],
  shouldZoom = true,
}: {
  map?: google.maps.Map | null;
  mapElements?: MapElement[];
  padding?: number;
  shouldZoom?: boolean;
}): void => {
  const isInitialZoom = useRef(false);

  useEffect(() => {
    if (!map || mapElements.length === 0 || isInitialZoom.current || !shouldZoom) return;

    const bounds = new google.maps.LatLngBounds();
    let pointsCount = 0;

    mapElements.forEach(mapElement => {
      switch (mapElement.type) {
        case 'pin': {
          bounds.extend(mapElement.center);
          pointsCount += 1;
          break;
        }
        case 'circle': {
          const { radius, center } = mapElement;
          const circle = new google.maps.Circle({ radius, center });
          const circleBounds = circle.getBounds();
          if (!circleBounds) break;
          bounds.extend(circleBounds.getNorthEast());
          bounds.extend(circleBounds.getSouthWest());
          pointsCount = Number.MAX_SAFE_INTEGER;
          break;
        }
        default: {
          break;
        }
      }
    });

    if (!pointsCount) return;

    isInitialZoom.current = true;

    if (pointsCount === 1) {
      map.fitBounds(extendSingleLatLngBounds(bounds, padding));
      return;
    }

    map.fitBounds(bounds, padding);
  }, [map, mapElements, padding, shouldZoom]);

  useEffect(
    () => (): void => {
      isInitialZoom.current = false;
    },
    [shouldZoom]
  );
};
